import { useHostPageIds } from "@/features/standalone-versions";
import { ANALYTICS_QUERY } from "@pages/analytics/constants";
import { useAnalyticsQuery } from "@pages/analytics/hooks";
import { useAnalyticsStoreShallow } from "@pages/analytics/store";
import { VersionType } from "@pages/analytics/types";
import { IconFile } from "@tabler/icons-react";
import moment from "moment";
import { useCallback, useMemo } from "react";

const dates = {
  from: moment().subtract(12, "month").format("YYYY-MM-DD"),
  to: moment().add(1, "days").format("YYYY-MM-DD"),
};

export const StoriesGroup = ({
  searchTerm,
  onClose,
}: {
  searchTerm: string;
  onClose: () => void;
}) => {
  const { setMultipleFilters } = useAnalyticsStoreShallow((s) => ({
    setMultipleFilters: s.setMultipleFilters,
  }));
  const { data: response, isLoading } = useAnalyticsQuery({
    query: [ANALYTICS_QUERY.VERSIONS],
    skipDebounce: true,
    enabled: searchTerm.length >= 3,
    overrides: {
      location: "",
      storyId: "",
      versionId: "",
      versionTitle: "",
      userId: "",
      dateFrom: dates.from,
      dateTo: dates.to,
      showUnread: false,
      folderId: "",
    },
  });
  const versions = useMemo(() => {
    const versionsData = response?.[ANALYTICS_QUERY.VERSIONS] as
      | VersionType[]
      | undefined;
    return versionsData || [];
  }, [response]);

  // The hidden page a user's standalone versions hang off is not a story and
  // is left out of this list; those versions still answer to the search's own
  // Versions group (see features/standalone-versions).
  const hostPageIds = useHostPageIds();
  const filteredVersions = useMemo(
    () =>
      filterVersions(versions, searchTerm).filter(
        (version) => !hostPageIds.has(version.story_id),
      ),
    [versions, searchTerm, hostPageIds],
  );

  const handleStoryChange = useCallback(
    (story: VersionType) => {
      setMultipleFilters({
        story: { id: story.story_id, title: story.page_title },
        location: "",
        version: { id: "", title: "" },
        user: { id: "", name: "" },
        folder: { id: "", name: "" },
      });
      onClose();
    },
    [setMultipleFilters, onClose],
  );
  return (
    <div className="tw-p-1 tw-border-b tw-border-b-solid tw-border-b-blue-100">
      <span className="tw-py-1 tw-px-2 tw-block tw-text-blue-500 tw-text-xs tw-font-sans">
        Stories
      </span>
      {isLoading ? (
        <StoriesSkeleton />
      ) : filteredVersions.length == 0 ? (
        <div className="tw-py-2 tw-text-center tw-text-sm tw-text-blue-500">
          {searchTerm ? "No stories found" : "No stories available"}
        </div>
      ) : (
        filteredVersions.map((version) => (
          <RowItem
            key={version.version_id}
            item={version}
            onStoryChange={handleStoryChange}
          />
        ))
      )}
    </div>
  );
};

const RowItem = ({
  item,
  onStoryChange,
}: {
  item: VersionType;
  onStoryChange: (story: VersionType) => void;
}) => {
  return (
    <button
      type="button"
      className="tw-py-1.5 tw-w-full tw-px-2 tw-outline-none tw-text-blue tw-text-sm tw-transition-colors hover:tw-bg-blue-50 tw-cursor-pointer tw-grid tw-grid-cols-[auto_1fr] tw-gap-2 tw-items-center tw-rounded"
      onClick={() => onStoryChange(item)}
    >
      <IconFile className="tw-shrink-0" />
      <span className="tw-truncate tw-text-left">{item.page_title}</span>
    </button>
  );
};

const SKELETON_WIDTHS = [65, 80, 45, 90, 55];

const StoriesSkeleton = () => {
  return (
    <>
      {SKELETON_WIDTHS.map((width) => (
        <div
          key={width}
          className="tw-flex tw-h-8 tw-w-full tw-items-center tw-justify-between tw-rounded tw-px-2 tw-animate-skeleton-loading"
        >
          <span
            className="tw-block tw-h-2 tw-w-24 tw-rounded-lg tw-bg-blue-100"
            style={{ width: `${width}%` }}
          />
        </div>
      ))}
    </>
  );
};

const filterVersions = (
  versions: VersionType[],
  searchTerm: string,
): VersionType[] => {
  if (!searchTerm.trim()) {
    // Filter out duplicates by story_id when no search term is given
    const uniqueMap = new Map<string, VersionType>();
    for (const v of versions) {
      if (v.story_id && !uniqueMap.has(v.story_id)) {
        uniqueMap.set(v.story_id, v);
      }
    }
    return Array.from(uniqueMap.values());
  }

  const lowerSearchTerm = searchTerm.toLowerCase().trim();

  // Filter, then ensure uniqueness by story_id
  const filtered = versions.filter((version) => {
    const storyTitle = (version.page_title || "").toLowerCase();
    return storyTitle.includes(lowerSearchTerm);
  });

  const uniqueMap = new Map<string, VersionType>();
  for (const v of filtered) {
    if (v.story_id && !uniqueMap.has(v.story_id)) {
      uniqueMap.set(v.story_id, v);
    }
  }
  return Array.from(uniqueMap.values());
};
